import type { FunctionComponent, SVGProps } from 'react';
import {
  CheckmarkIcon,
  StepCreateOrgIcon,
  StepHierarchyIcon,
  StepMappingIcon,
  StepOrgUserIcon,
} from '../assets/icons';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

interface WizardStep {
  title: string;
  Icon: IconType;
  iconClass: string;
}

/** The five wizard steps, with each glyph sized to its exact Figma dimensions. */
const STEPS: WizardStep[] = [
  { title: 'Create Organization', Icon: StepCreateOrgIcon, iconClass: 'h-[21px] w-[21px]' },
  { title: 'Define Organization Boundary', Icon: StepHierarchyIcon, iconClass: 'h-[21px] w-[23.333px]' },
  { title: 'Build Organization Hierarchy', Icon: StepHierarchyIcon, iconClass: 'h-[21px] w-[23.333px]' },
  { title: 'Create Level to EC Mapping', Icon: StepMappingIcon, iconClass: 'h-[18.667px] w-[23.333px]' },
  { title: 'Create Organization User', Icon: StepOrgUserIcon, iconClass: 'h-[18.667px] w-[25.667px]' },
];

const ACTIVE_RING =
  'shadow-[0px_0px_0px_4px_rgba(37,99,235,0.2),0px_4px_6px_-1px_rgba(0,74,198,0.2),0px_2px_4px_-2px_rgba(0,74,198,0.2)]';

interface OrgWizardStepperProps {
  /** Index (0-based) of the step currently in progress. */
  activeStep?: number;
  /** When provided, steps become clickable (e.g. to go back and edit). */
  onStepClick?: (index: number) => void;
  /** How many steps to render (defaults to all). Use to hide later steps. */
  visibleSteps?: number;
  /** Hide the "Pending" status text (e.g. in edit mode, where every step already has data). */
  hidePending?: boolean;
  /** Block clicking to steps ahead of the active one (e.g. during create — go back only). */
  disablePending?: boolean;
}

/** Shared wizard header. Scrolls horizontally on narrow screens. */
export default function OrgWizardStepper({
  activeStep = 0,
  onStepClick,
  visibleSteps,
  hidePending,
  disablePending,
}: OrgWizardStepperProps) {
  const steps = visibleSteps ? STEPS.slice(0, visibleSteps) : STEPS;
  return (
    <div className="overflow-x-auto pb-1">
      <div className="relative flex min-w-[720px] items-start justify-between gap-5 px-2 pt-1">
        {/* Connector line behind the circles */}
        <div className="absolute left-[10%] right-[10%] top-7 h-0.5 rounded-full bg-line/30" />
        {steps.map(({ title, Icon, iconClass }, i) => {
          const active = i === activeStep;
          const completed = i < activeStep;
          const pending = i > activeStep;
          const clickable = Boolean(onStepClick) && !(disablePending && pending);
          return (
            <button
              key={title}
              type="button"
              onClick={() => onStepClick?.(i)}
              disabled={!clickable}
              className={[
                'relative z-[1] flex w-[140px] flex-col items-center',
                pending ? 'opacity-70' : '',
                clickable ? 'cursor-pointer' : 'cursor-default',
              ].join(' ')}
            >
              <div
                className={[
                  'flex size-14 items-center justify-center rounded-full',
                  active
                    ? `bg-brand text-white ${ACTIVE_RING}`
                    : completed
                      ? 'bg-brand text-white'
                      : 'bg-[#e0e3e5] text-ink-700',
                ].join(' ')}
              >
                {completed ? <CheckmarkIcon className="size-7" /> : <Icon className={iconClass} />}
              </div>
              <div className="flex flex-col items-center gap-1 pt-4 text-center">
                <p
                  className={
                    active
                      ? 'text-[18px] font-medium leading-[22px] text-ink'
                      : completed
                        ? 'text-base font-medium leading-5 text-ink'
                        : 'text-base leading-5 text-ink-secondary'
                  }
                >
                  {title}
                </p>
                {!(pending && hidePending) && (
                  <p
                    className={[
                      'text-sm font-medium leading-5',
                      pending ? 'text-ink-secondary' : 'text-ink',
                    ].join(' ')}
                  >
                    {active ? 'In Progress' : completed ? 'Completed' : 'Pending'}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
